import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import instance from '../../api/Axios';

const BursaryComponents = () => {
  const [componentTypes, setComponentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchComponentTypes();
  }, []);

  const fetchComponentTypes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await instance.get('/api/component-types/');
      setComponentTypes(response.data);
    } catch (error) {
      console.error('Error fetching component types:', error);
      setError('Failed to load component types. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Component name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Component code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
    }

    // Check for duplicate names and codes (excluding current item when editing)
    const existingNames = componentTypes
      .filter(ct => editingComponent ? ct.id !== editingComponent.id : true)
      .map(ct => ct.name.toLowerCase());
    
    const existingCodes = componentTypes
      .filter(ct => editingComponent ? ct.id !== editingComponent.id : true)
      .map(ct => ct.code.toLowerCase());

    if (existingNames.includes(formData.name.toLowerCase())) {
      newErrors.name = 'A component with this name already exists';
    }

    if (existingCodes.includes(formData.code.toLowerCase())) {
      newErrors.code = 'A component with this code already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim(),
        is_active: formData.is_active
      };

      if (editingComponent) {
        // Update existing component type
        await instance.put(`/api/component-types/${editingComponent.id}/`, payload);
        setSuccess('Component type updated successfully!');
      } else {
        // Create new component type
        await instance.post('/api/component-types/', payload);
        setSuccess('Component type created successfully!');
      }

      // Reset form and close modal
      setFormData({
        name: '',
        code: '',
        description: '',
        is_active: true
      });
      setEditingComponent(null);
      setModal(false);
      
      // Refresh the list
      fetchComponentTypes();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error saving component type:', error);
      if (error.response?.data) {
        // Handle specific field errors from backend
        const backendErrors = error.response.data;
        if (typeof backendErrors === 'object') {
          setErrors(backendErrors);
        } else {
          setError(backendErrors.detail || 'Failed to save component type. Please try again.');
        }
      } else {
        setError('Failed to save component type. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (componentType) => {
    setEditingComponent(componentType);
    setFormData({
      name: componentType.name,
      code: componentType.code,
      description: componentType.description || '',
      is_active: componentType.is_active
    });
    setErrors({});
    setError('');
    setModal(true);
  };

  const handleDelete = async (componentType) => {
    if (!window.confirm(`Are you sure you want to delete "${componentType.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await instance.delete(`/api/component-types/${componentType.id}/`);
      setSuccess('Component type deleted successfully!');
      fetchComponentTypes();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting component type:', error);
      setError('Failed to delete component type. It may be in use by existing bursaries.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleStatus = async (componentType) => {
    try {
      const payload = {
        ...componentType,
        is_active: !componentType.is_active
      };

      await instance.put(`/api/component-types/${componentType.id}/`, payload);
      setSuccess(`Component type ${payload.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchComponentTypes();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating component status:', error);
      setError('Failed to update component status. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const openCreateModal = () => {
    setEditingComponent(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true
    });
    setErrors({});
    setError('');
    setModal(true);
  };

  const generateCodeFromName = (name) => {
    // Generate code from name: remove spaces, special chars, convert to uppercase
    return name
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toUpperCase()
      .substring(0, 20); // Limit to 20 characters
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name: name,
      // Auto-generate code if creating new component and code is empty
      code: !editingComponent && !prev.code ? generateCodeFromName(name) : prev.code
    }));
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading component types...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Bursary Component Types</h2>
          <small className="text-muted">Manage the types of components that can be added to bursaries</small>
        </div>
        <Button color="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Add Component Type
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert color="success" className="mb-4">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {error && (
        <Alert color="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Component Types Table */}
      <Card>
        <CardHeader>
          <CardTitle tag="h5" className="mb-0">
            <i className="bi bi-boxes me-2"></i>
            Component Types ({componentTypes.length})
          </CardTitle>
        </CardHeader>
        <CardBody>
          {componentTypes.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-boxes" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">No Component Types</h5>
              <p>Get started by creating your first component type.</p>
              <Button color="primary" onClick={openCreateModal}>
                <i className="bi bi-plus-circle me-2"></i>
                Create First Component Type
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {componentTypes.map((componentType) => (
                    <tr key={componentType.id}>
                      <td>
                        <strong>{componentType.name}</strong>
                      </td>
                      <td>
                        <Badge color="secondary" pill>
                          {componentType.code}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {componentType.description || '-'}
                        </small>
                      </td>
                      <td>
                        <Badge 
                          color={componentType.is_active ? 'success' : 'warning'}
                          pill
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleStatus(componentType)}
                          title="Click to toggle status"
                        >
                          {componentType.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(componentType.created_at).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle size="sm" color="outline-primary">
                            <i className="bi bi-three-dots-vertical"></i>
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => handleEdit(componentType)}>
                              <i className="bi bi-pencil me-2"></i>
                              Edit
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem 
                              onClick={() => handleDelete(componentType)}
                              className="text-danger"
                            >
                              <i className="bi bi-trash me-2"></i>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModal(!modal)}>
            {editingComponent ? 'Edit Component Type' : 'Create New Component Type'}
          </ModalHeader>
          <ModalBody>
            {error && (
              <Alert color="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            <FormGroup>
              <Label for="name">
                Component Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                invalid={!!errors.name}
                placeholder="e.g., Laptop Allowance, Accommodation, Transport"
                maxLength={100}
              />
              {errors.name && <div className="text-danger small">{errors.name}</div>}
              <small className="form-text text-muted">
                A descriptive name for this component type
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="code">
                Component Code <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({...prev, code: e.target.value.toUpperCase()}))}
                invalid={!!errors.code}
                placeholder="e.g., LAPTOP, ACCOM, TRANSPORT"
                maxLength={20}
              />
              {errors.code && <div className="text-danger small">{errors.code}</div>}
              <small className="form-text text-muted">
                A short, unique code (uppercase letters, numbers, and underscores only)
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Optional description of what this component type covers..."
                rows={3}
                maxLength={500}
              />
              <small className="form-text text-muted">
                Optional detailed description ({formData.description.length}/500)
              </small>
            </FormGroup>

            <FormGroup check>
              <Input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
              />
              <Label check for="is_active">
                Active (available for use in bursaries)
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {editingComponent ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {editingComponent ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default BursaryComponents;